import { useEffect, useState } from 'react';
import { useConsent } from '../utils/consent';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const SITE_URL = 'https://www.klasowisko.pl';

// Organization JSON-LD - sitewide. Inject raz w <head> przy mount.
const ORGANIZATION_LD = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": "Klasowisko.pl",
    "url": SITE_URL,
    "logo": `${SITE_URL}/icons/icon-512x512.png`,
    "description": "Portal społecznościowy dla absolwentów i uczniów szkół w Polsce. Odnajdź klasę, kolegów, wspomnienia.",
    "foundingDate": "2025",
    "sameAs": [],
    "contactPoint": {
        "@type": "ContactPoint",
        "email": "bok@klasowisko.pl",
        "contactType": "customer support",
        "availableLanguage": ["Polish"]
    }
};

// WebSite z SearchAction - sitewide
const WEBSITE_LD = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "name": "Klasowisko.pl",
    "url": SITE_URL,
    "potentialAction": {
        "@type": "SearchAction",
        "target": `${SITE_URL}/search?q={search_term_string}`,
        "query-input": "required name=search_term_string"
    }
};

function installSitewideLd() {
    if (typeof document === 'undefined') return;
    // Idempotentne - sprawdzamy data-attribute
    [{ key: 'organization', data: ORGANIZATION_LD }, { key: 'website', data: WEBSITE_LD }].forEach(({ key, data }) => {
        if (document.querySelector(`script[data-seo-sitewide="${key}"]`)) return;
        const s = document.createElement('script');
        s.type = 'application/ld+json';
        s.setAttribute('data-seo-sitewide', key);
        s.textContent = JSON.stringify(data);
        document.head.appendChild(s);
    });
}

/**
 * SEOHead - laduje globalne ustawienia SEO (meta_title, description, keywords, OG image,
 * Google Search Console verification) ORAZ - tylko po zgodzie uzytkownika -
 * Google Analytics 4 i Google AdSense.
 *
 * Consent flow:
 *  - Domyslnie consent.analytics = false, consent.ads = false (Google Consent Mode v2 defaults
 *    zainstalowane wczesniej z index.js installConsentDefaults()).
 *  - GA4 ladowany tylko gdy consent.analytics === true.
 *  - AdSense ladowany tylko gdy consent.ads === true.
 *  - Reagujemy na zmiane consent (event 'klaso:consent-change' przez useConsent).
 */
export function SEOHead() {
    const [settings, setSettings] = useState(null);
    const [metaApplied, setMetaApplied] = useState(false);
    const [gaLoaded, setGaLoaded] = useState(false);
    const [adsLoaded, setAdsLoaded] = useState(false);
    const consent = useConsent();

    // 0) Sitewide JSON-LD (Organization + WebSite) - jednorazowo
    useEffect(() => {
        installSitewideLd();
    }, []);

    // 1) Pobierz globalne ustawienia raz
    useEffect(() => {
        let aborted = false;
        (async () => {
            try {
                const response = await fetch(`${API_URL}/api/settings/seo`);
                if (!aborted && response.ok) {
                    setSettings(await response.json());
                }
            } catch (e) {
                console.warn('SEOHead: nie udalo sie pobrac /api/settings/seo', e);
            }
        })();
        return () => { aborted = true; };
    }, []);

    // 2) Meta tagi i GSC verification - zawsze (bez consent)
    useEffect(() => {
        if (!settings || metaApplied) return;

        if (settings.meta_title) document.title = settings.meta_title;

        if (settings.meta_description) {
            const m = document.querySelector('meta[name="description"]');
            if (m) m.setAttribute('content', settings.meta_description);
        }

        if (settings.keywords && settings.keywords.length > 0) {
            const m = document.querySelector('meta[name="keywords"]');
            if (m) m.setAttribute('content', settings.keywords.join(', '));
        }

        if (settings.og_image) {
            let og = document.querySelector('meta[property="og:image"]');
            if (!og) {
                og = document.createElement('meta');
                og.setAttribute('property', 'og:image');
                document.head.appendChild(og);
            }
            og.setAttribute('content', settings.og_image);
        }

        if (settings.google_console_verification) {
            const v = settings.google_console_verification
                .replace('google-site-verification=', '')
                .replace(/[<>"']/g, '');
            if (v) {
                let m = document.querySelector('meta[name="google-site-verification"]');
                if (!m) {
                    m = document.createElement('meta');
                    m.setAttribute('name', 'google-site-verification');
                    document.head.appendChild(m);
                }
                m.setAttribute('content', v);
            }
        }

        setMetaApplied(true);
    }, [settings, metaApplied]);

    // 3) Google Analytics - tylko po consent.analytics
    useEffect(() => {
        if (!settings || gaLoaded) return;
        if (!consent.analytics) return;
        const gaId = (settings.google_analytics_id || '').trim();
        if (!gaId) return;

        if (!document.querySelector(`script[data-klaso="ga"]`)) {
            const s = document.createElement('script');
            s.async = true;
            s.src = `https://www.googletagmanager.com/gtag/js?id=${gaId}`;
            s.setAttribute('data-klaso', 'ga');
            document.head.appendChild(s);

            const init = document.createElement('script');
            init.setAttribute('data-klaso', 'ga-init');
            init.innerHTML = `
                window.dataLayer = window.dataLayer || [];
                function gtag(){dataLayer.push(arguments);}
                window.gtag = window.gtag || gtag;
                gtag('js', new Date());
                gtag('config', '${gaId}', { anonymize_ip: true });
            `;
            document.head.appendChild(init);
        }
        setGaLoaded(true);
    }, [settings, consent.analytics, gaLoaded]);

    // 4) AdSense - tylko po consent.ads
    useEffect(() => {
        if (!settings || adsLoaded) return;
        if (!consent.ads) return;
        const code = (settings.adsense_code || '').trim();
        if (!code) return;

        const m = code.match(/client=([^"&\s]+)/);
        const clientId = m ? m[1] : null;
        if (!clientId) return;

        if (!document.querySelector('script[data-klaso="adsense"]')) {
            const s = document.createElement('script');
            s.async = true;
            s.src = `https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=${clientId}`;
            s.crossOrigin = 'anonymous';
            s.setAttribute('data-klaso', 'adsense');
            document.head.appendChild(s);
        }
        // Daj reszcie aplikacji wiedziec, ze AdSense jest gotowy
        window.__klasoAdSenseClient = clientId;
        setAdsLoaded(true);
    }, [settings, consent.ads, adsLoaded]);

    return null;
}

export default SEOHead;
